import type {
  OfflineBranch,
  OfflineCatalogSnapshot,
  OfflineProduct,
  OfflineInventoryRow,
  OfflinePosPersistedState,
} from '../types.js';
import type { SqliteDatabase } from './database.js';
import { setSyncMetaVersion } from './database.js';

export function upsertProduct(db: SqliteDatabase, product: OfflineProduct): void {
  const now = new Date().toISOString();
  db.prepare(
    `INSERT INTO products (
      visible_id, name, sku, barcode, description, sale_price_ht, tax_rate,
      category_id, is_active, updated_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    ON CONFLICT(visible_id) DO UPDATE SET
      name = excluded.name, sku = excluded.sku, barcode = excluded.barcode,
      description = excluded.description, sale_price_ht = excluded.sale_price_ht,
      tax_rate = excluded.tax_rate, category_id = excluded.category_id,
      is_active = excluded.is_active, updated_at = excluded.updated_at`,
  ).run(
    product.visibleId,
    product.name,
    product.sku,
    product.barcode ?? null,
    product.description ?? null,
    product.salePriceHT,
    product.taxRate,
    product.categoryId,
    product.isActive ? 1 : 0,
    now,
  );
}

export function deleteProduct(db: SqliteDatabase, visibleId: string): void {
  db.prepare(`DELETE FROM products WHERE visible_id = ?`).run(visibleId);
}

export function upsertCategory(
  db: SqliteDatabase,
  category: OfflineCatalogSnapshot['categories'][number],
): void {
  const now = new Date().toISOString();
  db.prepare(
    `INSERT INTO categories (
      visible_id, name, is_active, is_free_sale_or_service, is_free_price, updated_at
    ) VALUES (?, ?, ?, ?, ?, ?)
    ON CONFLICT(visible_id) DO UPDATE SET
      name = excluded.name, is_active = excluded.is_active,
      is_free_sale_or_service = excluded.is_free_sale_or_service,
      is_free_price = excluded.is_free_price, updated_at = excluded.updated_at`,
  ).run(
    category.visibleId,
    category.name,
    category.isActive ? 1 : 0,
    category.isFreeSaleOrService ? 1 : 0,
    category.isFreePrice ? 1 : 0,
    now,
  );
}

export function deleteCategory(db: SqliteDatabase, visibleId: string): void {
  db.prepare(`DELETE FROM categories WHERE visible_id = ?`).run(visibleId);
}

export function upsertInventory(db: SqliteDatabase, row: OfflineInventoryRow): void {
  const now = new Date().toISOString();
  db.prepare(
    `INSERT INTO branch_inventory (branch_id, product_id, stock, exposed_on_site, updated_at)
     VALUES (?, ?, ?, ?, ?)
     ON CONFLICT(branch_id, product_id) DO UPDATE SET
       stock = excluded.stock, exposed_on_site = excluded.exposed_on_site, updated_at = excluded.updated_at`,
  ).run(row.branchId, row.productId, row.stock, row.exposedOnSite ?? null, now);
}

export function deleteInventory(db: SqliteDatabase, branchId: string, productId: string): void {
  db.prepare(`DELETE FROM branch_inventory WHERE branch_id = ? AND product_id = ?`).run(
    branchId,
    productId,
  );
}

export function loadCatalogSnapshot(
  db: SqliteDatabase,
  branchId: string,
): OfflineCatalogSnapshot | null {
  const products = db
    .prepare(`SELECT * FROM products WHERE is_active = 1`)
    .all() as Array<{
      visible_id: string;
      name: string;
      sku: string;
      barcode: string | null;
      description: string | null;
      sale_price_ht: number;
      tax_rate: number;
      category_id: string;
      is_active: number;
    }>;

  if (products.length === 0) {
    const catCount = (
      db.prepare(`SELECT COUNT(*) as c FROM categories`).get() as { c: number }
    ).c;
    if (catCount === 0) return null;
  }

  const categories = db.prepare(`SELECT * FROM categories WHERE is_active = 1`).all() as Array<{
    visible_id: string;
    name: string;
    is_active: number;
    is_free_sale_or_service: number;
    is_free_price: number;
  }>;

  const inventories = db
    .prepare(`SELECT * FROM branch_inventory WHERE branch_id = ?`)
    .all(branchId) as Array<{
      branch_id: string;
      product_id: string;
      stock: number;
      exposed_on_site: number | null;
    }>;

  const syncedAt =
    (db.prepare(`SELECT value FROM kv_meta WHERE key = 'last_sync_at'`).get() as
      | { value: string }
      | undefined)?.value ?? new Date().toISOString();

  return {
    branchId,
    syncedAt,
    products: products.map((p) => ({
      visibleId: p.visible_id,
      name: p.name,
      sku: p.sku,
      barcode: p.barcode,
      description: p.description,
      salePriceHT: p.sale_price_ht,
      taxRate: p.tax_rate,
      categoryId: p.category_id,
      isActive: p.is_active === 1,
    })),
    categories: categories.map((c) => ({
      visibleId: c.visible_id,
      name: c.name,
      isActive: c.is_active === 1,
      isFreeSaleOrService: c.is_free_sale_or_service === 1,
      isFreePrice: c.is_free_price === 1,
    })),
    inventories: inventories.map((i) => ({
      productId: i.product_id,
      branchId: i.branch_id,
      stock: i.stock,
      exposedOnSite: i.exposed_on_site ?? undefined,
    })),
  };
}

export function searchProductsSql(
  db: SqliteDatabase,
  query: string,
  _branchId: string,
): OfflineProduct[] {
  const q = `%${query.trim().toLowerCase()}%`;
  const rows = db
    .prepare(
      `SELECT p.* FROM products p
       WHERE p.is_active = 1 AND (
         LOWER(p.name) LIKE ? OR LOWER(p.sku) LIKE ? OR LOWER(COALESCE(p.barcode, '')) LIKE ?
       )
       ORDER BY p.name ASC LIMIT 200`,
    )
    .all(q, q, q) as Array<{
      visible_id: string;
      name: string;
      sku: string;
      barcode: string | null;
      description: string | null;
      sale_price_ht: number;
      tax_rate: number;
      category_id: string;
      is_active: number;
    }>;

  return rows.map((p) => ({
    visibleId: p.visible_id,
    name: p.name,
    sku: p.sku,
    barcode: p.barcode,
    description: p.description,
    salePriceHT: p.sale_price_ht,
    taxRate: p.tax_rate,
    categoryId: p.category_id,
    isActive: p.is_active === 1,
  }));
}

export function applyLocalStockDeltaSql(
  db: SqliteDatabase,
  branchId: string,
  items: Array<{ productId: string; quantity: number }>,
  direction: 'sale' | 'refund',
): void {
  const sign = direction === 'sale' ? -1 : 1;
  const stmt = db.prepare(
    `UPDATE branch_inventory SET stock = stock + ? WHERE branch_id = ? AND product_id = ?`,
  );
  for (const item of items) {
    stmt.run(sign * item.quantity, branchId, item.productId);
  }
}

export function cacheBranchesSql(db: SqliteDatabase, branches: OfflineBranch[]): void {
  const del = db.prepare(`DELETE FROM branches`);
  const ins = db.prepare(
    `INSERT INTO branches (visible_id, name, code, address, is_main_branch)
     VALUES (?, ?, ?, ?, ?)`,
  );
  const tx = db.transaction(() => {
    del.run();
    for (const b of branches) {
      ins.run(b.visibleId, b.name, b.code, b.address ?? null, b.isMainBranch ? 1 : 0);
    }
  });
  tx();
}

export function getCachedBranchesSql(db: SqliteDatabase): OfflineBranch[] {
  const rows = db.prepare(`SELECT * FROM branches ORDER BY name ASC`).all() as Array<{
    visible_id: string;
    name: string;
    code: string;
    address: string | null;
    is_main_branch: number;
  }>;
  return rows.map((b) => ({
    visibleId: b.visible_id,
    name: b.name,
    code: b.code,
    address: b.address ?? undefined,
    isMainBranch: b.is_main_branch === 1,
  }));
}

export function setCatalogMeta(db: SqliteDatabase, branchId: string): void {
  const now = new Date().toISOString();
  db.prepare(
    `INSERT INTO kv_meta (key, value) VALUES ('last_sync_at', ?)
     ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
  ).run(now);
  db.prepare(
    `INSERT INTO kv_meta (key, value) VALUES ('catalog_branch_id', ?)
     ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
  ).run(branchId);
}

export function importJsonStateV2(db: SqliteDatabase, state: OfflinePosPersistedState): void {
  const tx = db.transaction(() => {
    if (state.catalog) {
      for (const p of state.catalog.products) upsertProduct(db, p);
      for (const c of state.catalog.categories) upsertCategory(db, c);
      for (const i of state.catalog.inventories) upsertInventory(db, i);
      setCatalogMeta(db, state.catalog.branchId);
    }
    for (const b of state.branches) {
      db.prepare(
        `INSERT OR REPLACE INTO branches (visible_id, name, code, address, is_main_branch)
         VALUES (?, ?, ?, ?, ?)`,
      ).run(b.visibleId, b.name, b.code, b.address ?? null, b.isMainBranch ? 1 : 0);
    }
    for (const p of state.pending) {
      db.prepare(
        `INSERT OR IGNORE INTO outbox_ops (
          client_op_id, kind, cart_id, upload_status, failed_attempts,
          payload_json, local_receipt_id, created_at, last_error
        ) VALUES (?, ?, ?, 'PENDING', ?, ?, ?, ?, ?)`,
      ).run(
        p.clientOpId,
        p.kind,
        p.kind === 'sale' ? (p.payload as { cartId: string }).cartId : p.clientOpId,
        p.attempts,
        JSON.stringify(p.payload),
        p.localReceiptId ?? null,
        p.createdAt,
        p.lastError ?? null,
      );
    }
    for (const h of state.salesHistory) {
      db.prepare(
        `INSERT OR REPLACE INTO sales_history (
          id, sale_visible_id, receipt_number, cart_id, branch_id, cashier_name,
          items_json, subtotal, discount_percentage, discount_amount, grand_total,
          payment_method, cash_given, cash_returned, created_at, status, pending_sync
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      ).run(
        h.id,
        h.saleVisibleId,
        h.receiptNumber,
        h.cartId,
        h.branchId,
        h.cashierName,
        JSON.stringify(h.items),
        h.subtotal,
        h.discountPercentage,
        h.discountAmount,
        h.grandTotal,
        h.paymentMethod,
        h.cashGiven,
        h.cashReturned,
        h.createdAt,
        h.status,
        h.pendingSync ? 1 : 0,
      );
    }
    for (const profile of state.authProfiles) {
      db.prepare(
        `INSERT OR REPLACE INTO auth_profiles (
          username, branch_id, branch_name, user_json, credential_verifier,
          encrypted_refresh_token, last_online_login_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?)`,
      ).run(
        profile.username,
        profile.branchId,
        profile.branchName ?? null,
        JSON.stringify(profile.user),
        profile.credentialVerifier,
        profile.encryptedRefreshToken ?? null,
        profile.lastOnlineLoginAt,
      );
    }
    for (const key of ['products', 'categories', 'branchInventory', 'salesHistory', 'refunds', 'branches']) {
      setSyncMetaVersion(db, key, 0);
    }
  });
  tx();
}
